import { useRouter } from 'next/router';
import { setUserCity } from '@firsttable/functions';
import { type FilteredRestaurantsCarouselMode } from '@discover-data/gql/getters/filteredRestaurantsCarousel';
import { todayDateInTimeZone } from '@discover-data/stores/hooks/date/today';
import {
  MIN_CAROUSEL_RESTAURANTS,
  RestaurantCarousel,
  RestaurantCarouselSkeleton,
} from '@discover-components/restaurant/filteredCarousel/restaurantCarousel';
import useFilteredRestaurantsCarousel from '@discover-components/restaurant/filteredCarousel/useFilteredRestaurantsCarousel';
import Skeleton from '@discover-ui/skeleton';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import {
  PD6630_CAROUSEL_HEADER_CLASS,
  PD6630_CAROUSEL_HEADING_CLASS,
  PD6630_CAROUSEL_ITEM_CLASS,
  PD6630_CAROUSEL_TRACK_CLASS,
  PD6630SeeAllButton,
} from './PD6630CarouselHeader';
import PD6630RestaurantCard, { PD6630RestaurantCardSkeleton } from './PD6630RestaurantCard';
import type { PD6630Location } from './locations';

const HEADINGS: Record<FilteredRestaurantsCarouselMode, string> = {
  availableToday: 'Book tonight in',
  mostPopular: 'Most popular in',
  newArrivals: 'New venues in',
};

const FRAME_CLASS = {
  headerClassName: PD6630_CAROUSEL_HEADER_CLASS,
  headingClassName: PD6630_CAROUSEL_HEADING_CLASS,
  trackClassName: PD6630_CAROUSEL_TRACK_CLASS,
  itemClassName: PD6630_CAROUSEL_ITEM_CLASS,
};

function seeAllQuery(mode: FilteredRestaurantsCarouselMode, availableTodayDate?: string) {
  switch (mode) {
    case 'availableToday':
      return availableTodayDate ? `date=${availableTodayDate}` : '';
    case 'mostPopular':
      return 'sort=RANKING';
    case 'newArrivals':
      return 'sort=NEWEST';
    default: {
      const unreachable: never = mode;
      return unreachable;
    }
  }
}

export function PD6630RestaurantCarouselPlaceholder() {
  return (
    <RestaurantCarouselSkeleton
      {...FRAME_CLASS}
      heading={<Skeleton className="h-6 w-48 rounded sm:h-8" />}
      seeAll={<PD6630SeeAllButton disabled />}
      renderCard={() => <PD6630RestaurantCardSkeleton />}
    />
  );
}

export default function PD6630RestaurantCarousel({
  city,
  mode,
}: {
  city: PD6630Location;
  mode: FilteredRestaurantsCarouselMode;
}) {
  const router = useRouter();
  // Missing timezone must not use the browser's today: the listing drops a day-behind date to the week view.
  const availableTodayDate =
    mode === 'availableToday' && city.timeZone ? todayDateInTimeZone(city.timeZone) : undefined;
  const { restaurants, isLoading, hasError } = useFilteredRestaurantsCarousel({
    regionId: mode === 'availableToday' && !availableTodayDate ? undefined : city.id,
    mode,
    availableTodayDate,
  });
  const heading = `${HEADINGS[mode]} ${city.name}`;
  const unavailable = !isLoading && (hasError || restaurants.length < MIN_CAROUSEL_RESTAURANTS);

  if (unavailable) return null;

  return (
    <TrackProvider
      trackEvent={isLoading ? undefined : 'component_interaction'}
      section={heading.toLowerCase()}
      eventParams={{ component_type: 'carousel', action: 'viewed' }}>
      <section aria-busy={isLoading} className="relative w-full bg-transparent py-0">
        {isLoading ? (
          <RestaurantCarouselSkeleton
            {...FRAME_CLASS}
            heading={heading}
            seeAll={<PD6630SeeAllButton disabled />}
            renderCard={() => <PD6630RestaurantCardSkeleton />}
          />
        ) : (
          <RestaurantCarousel
            {...FRAME_CLASS}
            restaurants={restaurants}
            heading={heading}
            seeAll={
              <PD6630SeeAllButton
                onClick={() => {
                  setUserCity(city.slug);
                  const query = seeAllQuery(mode, availableTodayDate);
                  router.push(query ? `${city.slug}?${query}` : city.slug);
                }}
              />
            }
            renderCard={(restaurant, index) => (
              <PD6630RestaurantCard restaurant={restaurant} imagePriority={index < 2} />
            )}
          />
        )}
      </section>
    </TrackProvider>
  );
}
